/**
 * Run Store
 *
 * Persisted Zustand store for test run execution state.
 * activeRunMeta survives page refresh (persisted to localStorage).
 * runState is ephemeral — rebuilt from WebSocket on reconnection.
 *
 * @module store/automation/runStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { createPersistConfig } from '../middleware/persistenceMiddleware';
import logger from '../../shared/services/logging/logger';
import { resolveRunCounts } from '../../shared/utils/permutationStatus';
import { mergeSteps, mergeRecord, permKeyOf } from '../../shared/utils/mergeRunFrames';


  const INITIAL_STATE = {
    // Persisted — survives refresh
    activeRunMeta: null,                                                                                                                                                                                                                 
    // {
    //   runId, webSocketUrl, projectId, runnableId,
    //   tenantId, runnableFilePath, startedAt,
    //   logPath,     // S3 prefix for run logs  e.g. projects/P063/MyProject/TestSuite/runs/logs
    //   projectName, // used with logPath to build stream.json S3 key
    // }                                                                                                                                                                                                                                 
                                                                                                                                                                                                                                         
    // Ephemeral — rebuilt from WebSocket                                                                                                                                                                                                
    runState: null,                                         
    error: null,
    workerStatus: 'idle', // 'idle' | 'connecting' | 'connected' | 'streaming' | 'reconnecting' | 'disconnected'                                                                                                                         
                                                                                                                                                                                                                                         
    // Step cache — accumulates steps across all stream chunks/REST polls so                                                                                                                                                             
    // steps are never lost when a new payload arrives with a partial or empty                                                                                                                                                           
    // permutations array. Keyed by permutation key → ordered step array.                                                                                                                                                                
    // { [permKey: string]: step[] }                                                                                                                                                                                                     
    stepCache: {},                                                                                                                                                                                                                       
                                                                                                                                                                                                                                         
    // Permutation shells — each permutation minus its steps, so the table
    // keeps its rows when a frame arrives with `permutations: []`.
    // { [permKey: string]: permutation }
    permCache: {},
    // Flag: GlobalRunProgress requests AutomationPage to show results + select latest run                                                                                                                                               
    pendingViewResults: false,                              
  };                                                                                                                                                                                                                                     
                                                            
  export const useRunStore = create(
    persist(
      (set, get) => ({
        ...INITIAL_STATE,

        // Bumped by every startRun and deliberately outside INITIAL_STATE, so a
        // dismiss never resets it. A change means "a brand-new run was started"
        // — the signal the run windows use to close the previous run's window
        // (single or batch) before the new one opens. A resume after refresh
        // does not bump it.
        runNonce: 0,

        /**
         * Called after successful REST executeRun response.                                                                                                                                                                             
         * Stores metadata + sets initial 0% progress state.
         */                                                                                                                                                                                                                              
        startRun: (meta) => {
          logger.info('runStore: starting run', { runId: meta.runId });
          set({
            runNonce: get().runNonce + 1,
            activeRunMeta: meta,
            error: null,
            workerStatus: 'connecting',
            stepCache: {},
            permCache: {},
            runState: {
              runid: meta.runId,
              tenantId: meta.tenantId,
              projectName: meta.projectDisplayName || meta.projectName || '',
              testResults: {
                testSummary: {
                  runStatus: 'IN_PROGRESS',
                  runProgressPercentage: 0,
                  numberOfPermutations: 0,
                  numberOfPermutationsExecuted: 0,
                  numberOfPermutationsPassed: 0,
                  numberOfPermutationsFailed: 0,
                  numberOfPermutationsSkipped: 0,
                  numberOfPermutationsDisabled: 0,
                },
                permutations: [],
              },
            },
          });
        },

        /**
         * Replace runState with latest WebSocket snapshot.
         * If testSummary counts are zero but permutations exist with                                                                                                                                                                    
         * status data, recompute the counts from permutations so the                                                                                                                                                                    
         * progress bar always reflects the actual results.                                                                                                                                                                              
         */                                                                                                                                                                                                                              
        setRunState: (payload) => {
          // Always use the display name from activeRunMeta for UI — the WS/REST
          // payload has the S3 folder name baked in, not the display name.
          const authoritativeName = get().activeRunMeta?.projectDisplayName || get().activeRunMeta?.projectName;
          const enriched = authoritativeName
            ? { ...payload, projectName: authoritativeName }
            : payload;

          // ── Progress only ever moves forward ─────────────────────────────
          // Lambda delivery is asynchronous and unordered, so a late frame can
          // carry a lower percentage than one already applied — the captures
          // show 50% arriving after 100%. The bar must not run backwards.
          //
          // The matching guarantee for runStatus (never move back out of
          // COMPLETED) is enforced a layer up, in RunStreamService, which drops
          // post-completion frames and knows about sequence restarts; latching
          // it here as well could strand a reused runId as complete forever.
          const previousState = get().runState;
          const previousSummary = previousState?.runid === enriched?.runid
            ? previousState?.testResults?.testSummary
            : null;

          const clamped = (() => {
            const incoming = enriched?.testResults?.testSummary;
            if (!incoming || !previousSummary) return enriched;

            const highest = Math.max(
              previousSummary.runProgressPercentage ?? 0,
              incoming.runProgressPercentage ?? 0
            );
            if (highest === incoming.runProgressPercentage) return enriched;

            logger.debug('runStore: holding progress at its high-water mark', {
              incoming: incoming.runProgressPercentage,
              held: highest,
            });
            return {
              ...enriched,
              testResults: {
                ...enriched.testResults,
                testSummary: { ...incoming, runProgressPercentage: highest },
              },
            };
          })();

          const summary = clamped?.testResults?.testSummary;
          const perms = clamped?.testResults?.permutations;
   
          // ── Accumulate into the caches ────────────────────────────────────
          // A frame carries whatever fitted inside the 128 KB limit, which may
          // be fewer steps than last time, shorter bodies, no bodies, or no
          // permutations at all. Both caches therefore only ever grow: steps
          // are unioned by identity and merged field by field, and each
          // permutation's own fields are kept so the table survives a frame
          // that arrives with `permutations: []` but real progress.
          if (Array.isArray(perms) && perms.length > 0) {
            const currentCache = get().stepCache;
            const currentShells = get().permCache;
            const nextCache = { ...currentCache };
            const nextShells = { ...currentShells };
            let cacheChanged = false;

            perms.forEach((perm, pIdx) => {
              const permKey = permKeyOf(perm, pIdx);
              const incomingSteps = perm.steps || [];
              const existingSteps = currentCache[permKey] || [];

              if (incomingSteps.length > 0) {
                nextCache[permKey] = mergeSteps(existingSteps, incomingSteps);
                cacheChanged = true;
              }

              // The permutation without its steps — status, index, name. Merged
              // so a thinner frame cannot blank a status we already had.
              const { steps, ...shell } = perm;
              nextShells[permKey] = mergeRecord(currentShells[permKey], shell);
              cacheChanged = true;
            });

            if (cacheChanged) {
              set({ stepCache: nextCache, permCache: nextShells });
              logger.info('runStore: stepCache updated', {
                keys: Object.keys(nextCache),
                stepCounts: Object.fromEntries(Object.entries(nextCache).map(([k, v]) => [k, v.length])),
              });
            }
          }
                                                                                                                                                                                                                                         
          // ── Normalise the summary counters ────────────────────────────────
          // Two things are fixed here so every consumer reads correct numbers:
          //
          //  1. The TOTAL. In the stream, testSummary.numberOfPermutations reports
          //     how many have executed so far, not the grand total — mid-run (and
          //     even at COMPLETED) it can say 2 when there are really 4. Widened to
          //     the largest of: permutations.length, the sum of the per-status
          //     counters, and the reported total.
          //
          //  2. Missing per-status counters. When the backend hasn't sent them yet,
          //     they're tallied from permutations[] BY STATUS — never by hasPassed,
          //     which is true for SKIPPED and DISABLED permutations and would report
          //     them as passes.
          if (summary && Array.isArray(perms)) {
            const counts = resolveRunCounts(summary, perms);
            const needsFix =
              summary.numberOfPermutations !== counts.total ||
              summary.numberOfPermutationsExecuted !== counts.executed ||
              summary.numberOfPermutationsPassed !== counts.passed ||
              summary.numberOfPermutationsFailed !== counts.failed ||
              summary.numberOfPermutationsSkipped !== counts.skipped ||
              summary.numberOfPermutationsDisabled !== counts.disabled;

            if (needsFix) {
              logger.debug('runStore: normalising summary counts', {
                reported: {
                  total: summary.numberOfPermutations,
                  passed: summary.numberOfPermutationsPassed,
                  failed: summary.numberOfPermutationsFailed,
                },
                resolved: counts,
              });
              set({
                runState: {
                  ...clamped,
                  testResults: {
                    ...clamped.testResults,
                    testSummary: {
                      ...summary,
                      numberOfPermutations: counts.total,
                      numberOfPermutationsExecuted: counts.executed,
                      numberOfPermutationsPassed: counts.passed,
                      numberOfPermutationsFailed: counts.failed,
                      numberOfPermutationsSkipped: counts.skipped,
                      numberOfPermutationsDisabled: counts.disabled,
                    },
                  },
                },
              });
              return;
            }
          }

          set({ runState: clamped });
        },

        /**
         * Mark run as completed. Keep runState for display but
         * clear activeRunMeta from persistence (no reconnection needed).                                                                                                                                                                
         */                                                                                                                                                                                                                              
        completeRun: () => {                                                                                                                                                                                                             
          logger.info('runStore: run completed');                                                                                                                                                                                        
          set({                                             
            activeRunMeta: null,                                                                                                                                                                                                         
            workerStatus: 'disconnected',
          });                                                                                                                                                                                                                            
        },                                                  

        /**
         * User dismissed the progress bar — clear everything.
         */
        dismissRun: () => {
          logger.info('runStore: run dismissed');                                                                                                                                                                                        
          set(INITIAL_STATE);
        },                                                                                                                                                                                                                               
                                                            
        /**
         * Full reset (logout, store cleanup).
         */
        clearActiveRun: () => set(INITIAL_STATE),

        setError: (error) => set({ error }),
        setWorkerStatus: (workerStatus) => set({ workerStatus }),

        updateActiveRunMeta: (updates) => set(state => ({
          activeRunMeta: state.activeRunMeta ? { ...state.activeRunMeta, ...updates } : null,
        })),
                                                                                                                                                                                                                                         
        /**                                                 
         * Signal AutomationPage to switch to results view and select the latest run.
         * Used by GlobalRunProgress when user clicks the "Results" button.
         */                                                                                                                                                                                                                              
        requestViewResults: () => set({ pendingViewResults: true }),
        clearViewResults: () => set({ pendingViewResults: false }),                                                                                                                                                                      
      }),                                                   
      createPersistConfig('cliffi-run-store', {
        // The active run must actually come back after a refresh — the default
        // adapter saves it but never restores it
        restorable: true,
        partialize: (state) => ({
          activeRunMeta: state.activeRunMeta,                                                                                                                                                                                            
        }),
      })                                                                                                                                                                                                                                 
    )                                                       
  );

  // ── Granular Selector Hooks ─────────────────────────────────────

  /** Changes once per newly started run — key a run window on it to remount fresh. */
  export const useRunNonce = () =>
    useRunStore(state => state.runNonce);

  export const useRunState = () =>
    useRunStore(state => state.runState);
                                                                                                                                                                                                                                         
  export const useStepCache = () =>
    useRunStore(state => state.stepCache);

  /** Each permutation minus its steps, kept so an empty frame cannot blank the table. */
  export const usePermCache = () =>
    useRunStore(state => state.permCache);                                                                                                                                                                                            
                                                            
  export const useIsRunActive = () =>
    useRunStore(state => state.activeRunMeta !== null || state.runState !== null);
                                                                                                                                                                                                                                         
  export const useIsRunning = () =>
    useRunStore(state =>                                                                                                                                                                                                                 
      state.runState?.testResults?.testSummary?.runStatus === 'IN_PROGRESS'
    );

  export const useIsCompleted = () =>
    useRunStore(state => {
      const rs = state.runState;                                                                                                                                                                                                         
      if (!rs) return false;
      // WebSocket format                                                                                                                                                                                                                
      if (rs.testResults?.testSummary?.runStatus === 'COMPLETED') return true;
      // REST API format (top-level result field)
      if (rs.result === 'PASSED' || rs.result === 'FAILED' || rs.result === 'ERROR') return true;                                                                                                                                        
      return false;
    });                                                                                                                                                                                                                                  
                                                            
  export const useHasFailures = () =>
    useRunStore(state => {
      const testResults = state.runState?.testResults;
      if (!testResults?.testSummary) return false;
      // Only a FAILED permutation is a failure — skipped and disabled are not.
      return resolveRunCounts(testResults.testSummary, testResults.permutations).failed > 0;
    });                                                   

  export const useWorkerStatus = () =>                                                                                                                                                                                                   
    useRunStore(state => state.workerStatus);
                                                                                                                                                                                                                                         
  export const useRunError = () =>                          
    useRunStore(state => state.error);

  export const useActiveRunMeta = () =>
    useRunStore(state => state.activeRunMeta);

  export const usePendingViewResults = () =>                                                                                                                                                                                             
    useRunStore(state => state.pendingViewResults);
                                                                                                                                                                                                                                         
  // Counts are resolved by status (see shared/utils/permutationStatus) rather than
  // read straight off testSummary, so consumers get the corrected total and the
  // skipped / disabled breakdown even if setRunState hasn't normalised yet.
  export const useRunProgress = () =>
    useRunStore(
      useShallow(state => {
        const testResults = state.runState?.testResults;
        const counts = resolveRunCounts(testResults?.testSummary, testResults?.permutations);
        return {
          percentage: testResults?.testSummary?.runProgressPercentage ?? 0,
          executed: counts.executed,
          total: counts.total,
          passed: counts.passed,
          failed: counts.failed,
          skipped: counts.skipped,
          disabled: counts.disabled,
          passRate: counts.passRate,
        };
      })
    );                                                                                                                                                                                                                                 
                                                            
  export const useRunActions = () =>                                                                                                                                                                                                     
    useRunStore(
      useShallow(state => ({
        startRun: state.startRun,
        setRunState: state.setRunState,
        completeRun: state.completeRun,
        dismissRun: state.dismissRun,
        clearActiveRun: state.clearActiveRun,
        setError: state.setError,
        setWorkerStatus: state.setWorkerStatus,
        requestViewResults: state.requestViewResults,
        clearViewResults: state.clearViewResults,
        updateActiveRunMeta: state.updateActiveRunMeta,
      }))                                                                                                                                                                                                                                
    );